import * as Sentry from "@sentry/react";
import pusher from "config/pusher";
import qs from "qs";
import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { getCountData, postMe } from "services/api/taptype/auth";
import { getGlobals, postCheckMaintenance } from "services/api/taptype/home";
import { getSegments } from "services/api/taptype/segments";
import { actions as authActions, isAuthenticatedSelector, isUserDataCompleteSelector, userSelector } from "store/auth";
import { actions as countDataActions } from "store/count-data";
import { actions as flashesActions } from "store/flashes";
import { actions as globalsActions, globalsSelector } from "store/globals";
import { actions as segmentsActions, segmentsSelector } from "store/segments";
import { actions as uiActions } from "store/ui";
import {
	apiHandler,
	getCurrentSubdomain,
	getPharmacySegmentFromSegments,
	handleTokenFromQueryParams,
	isFunction,
	isJustmedsTenant,
	isOpenedInIframe,
	scrollToAnchor,
	scrollToTop,
	shouldShowPatientFacingStaffFlash,
	updateFaviconWithBadge,
} from "utils/helpers";
import { logRequisitionPdfDebug } from "utils/requisitionPdfDebug";
import vars from "utils/vars";
import useAxiosInterceptors from "views/hooks/useAxiosInterceptors";
import useLogoutIdleUser from "views/hooks/useLogoutIdleUser";
import useWhiteLabeling from "views/hooks/useWhiteLabeling";
import { default as ErrPage } from "views/pages/Err";
import Router from "views/router";
import LoadingFallback from "views/sections/LoadingFallback";
import RootToasts from "views/sections/RootToasts";

/** Segments under `/orders/…` that are not a note id (list index, store, edit flow). */
const RESERVED_ORDERS_PATH_SEGMENTS = new Set(["list", "store", "edit", "one-time"]);

/** Optional 2-letter locale prefix. Matches order/referral detail URLs used for requisition PDF toasts. */
const extractRequisitionPdfDetailNoteIdFromPathname = (pathnameValue) => {
	if (typeof pathnameValue !== "string") return "";
	const locale = "(?:[a-z]{2}/)?";
	const patterns = [
		new RegExp(`/${locale}user/lab-and-imaging/orders/([^/?#]+)`, "i"),
		new RegExp(`/${locale}user/lab-and-imaging/referrals/([^/?#]+)`, "i"),
		// Laboratory & imaging segment (medical): NotesCard / NotificationCard use `/orders/:noteId` under locale
		new RegExp(`/${locale}orders/([^/?#]+)`, "i"),
		// Laboratory & imaging: alternate path under /user/notes/orders/:noteId
		new RegExp(`/${locale}user/notes/orders/([^/?#]+)`, "i"),
	];
	for (const re of patterns) {
		const m = pathnameValue.match(re);
		const seg = m?.[1];
		if (!seg) continue;
		if (RESERVED_ORDERS_PATH_SEGMENTS.has(seg.toLowerCase())) continue;
		return seg;
	}
	return "";
};

/** Flatten common Laravel / Pusher `notification` payload shapes (see NotificationCard for API parity). */
const collectPusherUserNotificationLayers = (eventPayload) => {
	const root = eventPayload && typeof eventPayload === "object" ? eventPayload : {};
	const payload = root.payload != null && typeof root.payload === "object" ? root.payload : null;
	const inner = payload ?? root;
	const data =
		inner?.data != null && typeof inner.data === "object" && !Array.isArray(inner.data) ? inner.data : null;
	const nestedPayload =
		data?.payload != null && typeof data.payload === "object" && !Array.isArray(data.payload) ? data.payload : null;
	const layers = [nestedPayload, data, inner, root].filter((o) => o != null && typeof o === "object");
	return { root, inner, layers };
};

const pickFirstNonEmptyStringFromLayers = (layers, keys) => {
	for (const obj of layers) {
		for (const key of keys) {
			const v = obj[key];
			if (typeof v === "string" && v.trim() !== "") return v.trim();
		}
	}
	return "";
};

const pickFirstNoteOrOrderIdFromLayers = (layers) => {
	for (const obj of layers) {
		for (const key of ["note_id", "order_id"]) {
			const v = obj[key];
			if (v != null && String(v).trim() !== "") return String(v).trim();
		}
	}
	return "";
};

const REQUISITION_PDF_PUSHER_TYPES = new Set(["order_requisition_pdf_ready", "order_requisition_pdf_failed"]);

const parsePusherUserNotificationRequisitionPdf = (eventPayload) => {
	const { root, inner, layers } = collectPusherUserNotificationLayers(eventPayload);
	const typeField = pickFirstNonEmptyStringFromLayers(layers, ["type"]);
	const eventField = pickFirstNonEmptyStringFromLayers(layers, ["event"]);
	const rootEvent = typeof root.event === "string" && root.event.trim() !== "" ? root.event.trim() : "";
	const innerEvent = typeof inner.event === "string" && inner.event.trim() !== "" ? inner.event.trim() : "";
	const eventOrType =
		(REQUISITION_PDF_PUSHER_TYPES.has(typeField) ? typeField : "") ||
		(REQUISITION_PDF_PUSHER_TYPES.has(eventField) ? eventField : "") ||
		typeField ||
		eventField ||
		rootEvent ||
		innerEvent;
	const actionUrl = pickFirstNonEmptyStringFromLayers(layers, [
		"requisition_pdf_action_link",
		"download_url",
		"link",
	]);
	const title = pickFirstNonEmptyStringFromLayers(layers, ["title"]);
	const body = pickFirstNonEmptyStringFromLayers(layers, ["body", "message"]);
	const payloadNoteId = pickFirstNoteOrOrderIdFromLayers(layers);
	return { eventOrType, actionUrl, title, body, payloadNoteId };
};

const App = () => {
	const { i18n, t } = useTranslation();
	const { pathname, search } = useLocation();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	// Handle token from query params BEFORE any other logic or hooks
	// This ensures the token AND user placeholder are saved to localStorage before any API calls
	const tokenFromUrl = handleTokenFromQueryParams(search);

	// If a new token was found in URL, update Redux state immediately
	// This is necessary because Redux initial state was already set before this component rendered
	const hasTokenBeenUpdated = useRef(false);

	// Check if it's patient intake page
	const isPatientIntakePage = pathname.includes("/patient-intake");

	if (tokenFromUrl && !hasTokenBeenUpdated.current && !isPatientIntakePage) {
		// Re-sync Redux state with localStorage to pick up the new token and user
		const updatedToken = localStorage.getItem(vars.localStorage.fieldsName.token);
		const updatedUser = JSON.parse(localStorage.getItem(vars.localStorage.fieldsName.user) || "{}");

		if (updatedToken && updatedUser?.id) {
			// Update authentication state immediately to prevent redirections
			dispatch(
				authActions.updateAuthState({
					isAuthenticated: true,
					user: updatedUser,
				})
			);
			hasTokenBeenUpdated.current = true;
		}
	}

	const { segment_id, quote_id, token, auth_token, ...queryParams } = qs.parse(search, { ignoreQueryPrefix: true });
	// Support both 'token' and 'auth_token' parameter names
	const authToken = token || auth_token;

	// custom hooks
	useLogoutIdleUser();
	useAxiosInterceptors();

	// Initialize white labeling for the entire application
	const { isWhiteLabeled, customLogo, tenantConfig, isFullyConfigured } = useWhiteLabeling();

	// redux global state
	const user = useSelector(userSelector);
	const isAuthenticated = useSelector(isAuthenticatedSelector);
	const isUserDataComplete = useSelector(isUserDataCompleteSelector);
	const globals = useSelector(globalsSelector);
	const segments = useSelector(segmentsSelector);
	const countData = useSelector((state) => state?.countData || {});

	// constants
	const tenant = useMemo(() => getCurrentSubdomain(user?.tenant?.name || ""), [user?.tenant?.name]);
	const isGlobalLoaded = JSON.stringify(globals) !== JSON.stringify({});
	const isTenantJustmeds = isJustmedsTenant();
	const isPharmacistsPage = pathname === "/pharmacists";
	const isDemoTenant = Boolean(user?.tenant?.is_demo);
	const isInIframe = isOpenedInIframe();
	// a Team Moderator has no notifications/count data; the backend denies those calls for them
	const isUserTeamModerator = user?.position === vars.app.positions.teamModerator;

	// ref hook
	const globalsCancelRequestRef = useRef(null);
	const isMounted = useRef(false);
	const maintenanceModeCancelRequestRef = useRef(null);
	const segmentsCancelRequestRef = useRef(null);
	const userCancelRequestRef = useRef(null);
	const authCountDataCancelRequestRef = useRef(null);
	const pdfReadyPusherDedupeRef = useRef({ key: "", at: 0 });

	// state hook
	const [maintenanceModeState, setMaintenanceModeState] = useState(vars.isMaintenance);
	const [loggingUserLoadingState, setLoggingUserLoadingState] = useState(false);

	// memo hook
	const pharmacySegment = useMemo(() => getPharmacySegmentFromSegments(segments), [segments]);
	const globalsQuerySegmentId =
		pharmacySegment?.id && (isPharmacistsPage || segment_id === pharmacySegment?.id)
			? segment_id || pharmacySegment?.id
			: undefined;
	const globalsQuery = useMemo(
		() => (globalsQuerySegmentId ? { segment_id: globalsQuerySegmentId } : {}),
		[globalsQuerySegmentId]
	);
	const isAuthenticationRoute = useMemo(() => /\/auth(?:\/|$)/.test(pathname), [pathname]);

	// event handlers
	const onPageLoadGetGlobalsDataHandler = useCallback(
		async (query = {}) => {
			if (globalsCancelRequestRef.current?.signal) globalsCancelRequestRef.current?.abort();
			globalsCancelRequestRef.current = new AbortController();

			await apiHandler(getGlobals, {
				data: { query, options: { signal: globalsCancelRequestRef.current.signal } },
				onSuccess: (globals) => dispatch(globalsActions.update(globals)),
			});
		},
		[dispatch]
	);

	const onPageLoadGetSegmentsDataHandler = useCallback(async () => {
		dispatch(uiActions.setIsSegmentLoading(true));
		if (segmentsCancelRequestRef.current?.signal) segmentsCancelRequestRef.current?.abort();
		segmentsCancelRequestRef.current = new AbortController();

		await apiHandler(getSegments, {
			data: { options: { signal: segmentsCancelRequestRef.current.signal } },
			onError: () => dispatch(uiActions.setIsSegmentLoading(false)),
			onSuccess: (response) => {
				dispatch(segmentsActions.update(response?.segments || []));
				dispatch(uiActions.setIsSegmentLoading(false));
			},
		});
	}, [dispatch]);

	const onPageLoadGetUserDataHandler = useCallback(async (options = {}, successCb) => {
		if (userCancelRequestRef.current?.signal) userCancelRequestRef.current?.abort();
		userCancelRequestRef.current = new AbortController();

		await apiHandler(postMe, {
			data: { options: { signal: userCancelRequestRef.current.signal, ...(options || {}) } },
			onError: () => setLoggingUserLoadingState(false),
			onSuccess: (res) => {
				if (isFunction(successCb)) successCb(res);
				setLoggingUserLoadingState(false);
			},
		});
	}, []);

	const onPageLoadCheckForMaintenanceHandler = useCallback(async () => {
		if (maintenanceModeCancelRequestRef.current?.signal) maintenanceModeCancelRequestRef.current?.abort();
		maintenanceModeCancelRequestRef.current = new AbortController();

		await apiHandler(postCheckMaintenance, {
			data: { options: { signal: maintenanceModeCancelRequestRef.current.signal } },
			onSuccess: (res) => setMaintenanceModeState(res?.status || false),
		});
	}, []);

	const onPageLoadGetCountData = useCallback(async () => {
		if (!tenant) {
			return;
		}
		if (authCountDataCancelRequestRef.current?.signal) authCountDataCancelRequestRef.current?.abort();
		authCountDataCancelRequestRef.current = new AbortController();

		await apiHandler(getCountData, {
			data: {
				options: {
					signal: authCountDataCancelRequestRef.current.signal,
					headers: { "x-prevent-flashes": "true", "x-prevent-error-flashes": "true" },
				},
			},
			onError: () => {},
			onSuccess: (response) => dispatch(countDataActions.replace(response || {})),
		});
	}, [dispatch, tenant]);

	// effect hooks
	useEffect(() => {
		if (
			!maintenanceModeState &&
			((isAuthenticated && !globals?.demo_token) || (!isAuthenticated && globals?.demo_token))
		)
			onPageLoadGetUserDataHandler();
	}, [globals?.demo_token, isAuthenticated, maintenanceModeState, onPageLoadGetUserDataHandler, i18n.language]);

	useEffect(() => {
		// If token exists in URL and user is not authenticated, validate it immediately
		if (vars.isProduction && !isAuthenticated && authToken && !isDemoTenant && !quote_id && !isTenantJustmeds) {
			setLoggingUserLoadingState(true);
			onPageLoadGetUserDataHandler({ headers: { Authorization: `Bearer ${authToken}` } }, ({ user }) =>
				dispatch(authActions.login({ token: authToken, user }))
			);
		}
	}, [isAuthenticated, authToken, onPageLoadGetUserDataHandler, isDemoTenant, dispatch, quote_id, isTenantJustmeds]);

	useEffect(() => {
		onPageLoadGetGlobalsDataHandler(globalsQuery);
	}, [globalsQuery, i18n.language, isAuthenticated, onPageLoadGetGlobalsDataHandler]);

	useEffect(() => {
		onPageLoadGetSegmentsDataHandler();
	}, [i18n.language, onPageLoadGetSegmentsDataHandler]);

	useEffect(() => {
		onPageLoadCheckForMaintenanceHandler();
		if (![...(window.location.hash.split("#") || [])].filter(Boolean)?.length) scrollToTop();
	}, [onPageLoadCheckForMaintenanceHandler, pathname, queryParams.page]);

	useEffect(() => {
		if (isAuthenticated && isUserDataComplete && !isUserTeamModerator && !isAuthenticationRoute) {
			onPageLoadGetCountData();
		}
	}, [
		isAuthenticated,
		isAuthenticationRoute,
		isUserDataComplete,
		isUserTeamModerator,
		onPageLoadGetCountData,
		pathname,
	]);

	// Update favicon with notification badge
	useEffect(() => {
		const unreadNotificationsCount = +countData?.notifications?.unread_count || 0;
		updateFaviconWithBadge(unreadNotificationsCount);
	}, [countData?.notifications?.unread_count]);

	useEffect(() => {
		if (!isMounted?.current) {
			const message = "This link can't be accessed by current user. Please logout & try the link again.";
			if (isAuthenticated && shouldShowPatientFacingStaffFlash(pathname, isInIframe))
				dispatch(flashesActions.add({ type: "info", message, id: message }));
			isMounted.current = true;
		}
	}, [pathname, isAuthenticated, isInIframe, dispatch]);

	useEffect(() => {
		const isConnectionAllowed = isAuthenticated && !isDemoTenant && !isUserTeamModerator && user?.id && pusher;
		let channel;

		if (isConnectionAllowed) {
			// Update Pusher auth headers
			pusher.options.auth.headers["Authorization"] = `Bearer ${localStorage.getItem(
				vars.localStorage.fieldsName.token
			)}`;
			pusher.options.auth.headers["Tenant"] = tenant;

			const channelName = `private-user_${user.id}`;

			// Listen to Pusher connection states
			pusher.connection.bind("connecting", () => {});

			pusher.connection.bind("connected", () => {});

			pusher.connection.bind("unavailable", () => {
				console.error("❌ Pusher: Unavailable");
			});

			pusher.connection.bind("failed", () => {
				console.error("❌ Pusher: Connection Failed");
			});

			pusher.connection.bind("disconnected", () => {
				console.warn("⚠️ Pusher: Disconnected");
			});

			channel = pusher.subscribe(channelName);

			// Listen for subscription success
			channel.bind("pusher:subscription_succeeded", () => {});

			// Listen for subscription error
			channel.bind("pusher:subscription_error", (error) => {
				console.error("❌ Failed to subscribe to channel:", channelName, error);
			});

			channel.bind("InvalidateToken", ({ invalid_token = "" }) =>
				invalid_token === localStorage.getItem(vars.localStorage.fieldsName.token)
					? dispatch(authActions.logout())
					: false
			);

			const handlePusherNotification = (eventPayload = {}) => {
				const { eventOrType, actionUrl, payloadNoteId } =
					parsePusherUserNotificationRequisitionPdf(eventPayload);
				const currentOrderNoteId = extractRequisitionPdfDetailNoteIdFromPathname(pathname);
				const isCurrentOrderDetailsForPayload =
					Boolean(payloadNoteId) &&
					Boolean(currentOrderNoteId) &&
					String(currentOrderNoteId) === String(payloadNoteId);

				const isPdfReady = eventOrType === "order_requisition_pdf_ready";
				const isPdfFailed = eventOrType === "order_requisition_pdf_failed";

				if (isPdfReady || isPdfFailed) {
					const { layers } = collectPusherUserNotificationLayers(eventPayload);
					logRequisitionPdfDebug("pusher-pdf-notification", {
						eventOrType,
						hasActionUrl: Boolean(actionUrl),
						payloadNoteId,
						layerNoteId: pickFirstNonEmptyStringFromLayers(layers, ["note_id"]),
						layerOrderId: pickFirstNonEmptyStringFromLayers(layers, ["order_id"]),
						currentOrderNoteId,
						idsMatch:
							Boolean(payloadNoteId) &&
							Boolean(currentOrderNoteId) &&
							String(currentOrderNoteId) === String(payloadNoteId),
						isCurrentOrderDetailsForPayload,
						pathname,
						isPdfReady,
						isPdfFailed,
					});
				}

				if (isPdfReady) {
					const dedupeKey = `${payloadNoteId}\0${actionUrl}`;
					const now = Date.now();
					if (
						pdfReadyPusherDedupeRef.current.key === dedupeKey &&
						now - pdfReadyPusherDedupeRef.current.at < 2500
					) {
						onPageLoadGetCountData();
						dispatch(countDataActions.triggerNotificationsRefresh());
						return;
					}
					pdfReadyPusherDedupeRef.current = { key: dedupeKey, at: now };

					if (payloadNoteId) {
						try {
							sessionStorage.removeItem(`order-requisition-pdf-processing:${payloadNoteId}`);
						} catch {
							// ignore
						}
					}
					if (payloadNoteId && actionUrl) {
						try {
							const key = `order-requisition-pdf-ready:${payloadNoteId}`;
							sessionStorage.setItem(key, JSON.stringify({ actionUrl, ts: Date.now() }));
							window.dispatchEvent(
								new CustomEvent("order-requisition-pdf-ready", {
									detail: { noteId: payloadNoteId, actionUrl },
								})
							);
						} catch {
							// Ignore storage/dispatch issues; navigation/toasts are best-effort.
						}
					}
					if (isCurrentOrderDetailsForPayload && actionUrl) {
						navigate(`${pathname}${search || ""}`, {
							replace: true,
							state: {
								fromOrderPdfReadyNotification: true,
								requisitionPdfActionLink: actionUrl,
							},
						});
					}
				}

				if (isPdfFailed) {
					if (payloadNoteId) {
						try {
							sessionStorage.removeItem(`order-requisition-pdf-processing:${payloadNoteId}`);
						} catch {
							// ignore
						}
					}
				}

				onPageLoadGetCountData();
				dispatch(countDataActions.triggerNotificationsRefresh());
			};

			channel.bind("notification", handlePusherNotification);
			channel.bind(".notification", handlePusherNotification);
		}

		return () => {
			if (isConnectionAllowed && channel) {
				channel.unbind("InvalidateToken");
				channel.unbind("notification");
				channel.unbind(".notification");
				channel.unbind("pusher:subscription_succeeded");
				channel.unbind("pusher:subscription_error");
				channel.unsubscribe();
			}
		};
	}, [dispatch, isAuthenticated, isDemoTenant, user?.id, tenant, onPageLoadGetCountData, navigate, pathname, search]);

	useEffect(() => {
		// scroll to hash section in the URL
		scrollToAnchor();
		window.onhashchange = scrollToAnchor;
	}, [isGlobalLoaded, globals?.demo_token, isAuthenticated, pathname]);

	useEffect(() => {
		return () => {
			if (globalsCancelRequestRef.current?.signal) globalsCancelRequestRef.current?.abort();
			if (segmentsCancelRequestRef.current?.signal) segmentsCancelRequestRef.current?.abort();
			if (maintenanceModeCancelRequestRef.current?.signal) maintenanceModeCancelRequestRef.current?.abort();
			if (userCancelRequestRef.current?.signal) userCancelRequestRef.current?.abort();
			if (authCountDataCancelRequestRef.current?.signal) authCountDataCancelRequestRef.current?.abort();
		};
	}, []);

	return (
		<Suspense fallback={<LoadingFallback height="vh-100" allowLogo={!isInIframe} />}>
			<ErrorBoundary
				FallbackComponent={({ error, resetErrorBoundary }) => (
					<ErrPage
						code={500}
						codeMessage={t("system_messages.server_error_message")}
						message={error?.message}
						onReset={resetErrorBoundary}
					/>
				)}
				onReset={() => window.location.assign(window.location.origin)}>
				<RootToasts />
				{!isGlobalLoaded ||
				(isGlobalLoaded && globals?.demo_token && !isAuthenticated) ||
				loggingUserLoadingState ||
				(isAuthenticated && !isUserDataComplete) ? (
					<LoadingFallback height="vh-100" allowLogo={!isInIframe} />
				) : (
					<Router isMaintenanceMode={maintenanceModeState} />
				)}
			</ErrorBoundary>
		</Suspense>
	);
};

export default Sentry.withProfiler(App);
